import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Eye, Save, Calendar, Rocket, Globe, TestTube2 } from "lucide-react";

/**
 * R124 — Forum Daily Digest configuration + preview + one-shot send.
 *
 * Admin schedules a weekly community-forum digest email; R134 lets them
 * pick the exact timezone + local hour so the digest lands at 8am in the
 * resident's own city, not 8am UTC.
 */
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday",
                   "Friday", "Saturday", "Sunday"];
const DEFAULT_TZ_OPTIONS = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Phoenix", "America/Anchorage",
  "Pacific/Honolulu", "Europe/London", "Europe/Berlin", "Europe/Madrid",
  "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];

export default function ForumDigestPage() {
  const { propertyId } = useProperty();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewV2, setPreviewV2] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [chosenVariant, setChosenVariant] = useState(1);
  const [tzOptions, setTzOptions] = useState(DEFAULT_TZ_OPTIONS);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [runs, setRuns] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/forum-digest/config");
      setCfg(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
    try {
      const { data } = await api.get("/admin/forum-digest/timezones");
      if (data.timezones?.length) setTzOptions(data.timezones);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/ForumDigestPage.jsx: swallowed', e); }
    try {
      const { data } = await api.get("/admin/forum-digest/runs");
      setRuns(data.runs || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/ForumDigestPage.jsx: swallowed', e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/forum-digest/config", {
        enabled: !!cfg.enabled,
        day_of_week: parseInt(cfg.day_of_week ?? 0),
        hour_local: parseInt(cfg.hour_local ?? 8),
        timezone: cfg.timezone || "UTC",
        top_n: parseInt(cfg.top_n ?? 5),
        min_likes: parseInt(cfg.min_likes ?? 2),
      });
      toast.success("Digest schedule saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setSaving(false);
  };

  const doPreview = async () => {
    if (!propertyId) return toast.error("Select a property first");
    setPreviewLoading(true);
    try {
      const [{ data: v1 }, { data: v2 }] = await Promise.all([
        api.get(`/admin/forum-digest/preview?property_id=${propertyId}&variant=1`),
        api.get(`/admin/forum-digest/preview?property_id=${propertyId}&variant=2`),
      ]);
      setPreview(v1);
      setPreviewV2(v2);
      setChosenVariant(1);
    } catch (e) { toast.error(apiErrorMessage(e) || "Preview failed"); }
    setPreviewLoading(false);
  };

  const regenerateIntro = async (variant) => {
    if (!propertyId) return toast.error("Select a property first");
    setRegenerating(true);
    try {
      await api.post(
        `/admin/forum-digest/regenerate-intro?property_id=${propertyId}&variant=${variant}`);
      const { data: fresh } = await api.get(
        `/admin/forum-digest/preview?property_id=${propertyId}&variant=${variant}`);
      if (variant === 1) setPreview(fresh); else setPreviewV2(fresh);
      toast.success(`Variant ${variant} regenerated`);
    } catch (e) { toast.error(apiErrorMessage(e) || "Regenerate failed"); }
    setRegenerating(false);
  };

  const sendNow = async () => {
    if (!propertyId) return toast.error("Select a property first");
    if (!window.confirm("Send the digest to every resident of this community right now?")) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/forum-digest/send-now?property_id=${propertyId}`);
      if (data.skipped) {
        toast.info(`Nothing to feature — ${data.reason}`);
      } else {
        toast.success(`Sent to ${data.sent} resident${data.sent === 1 ? "" : "s"}` +
                       (data.failed ? ` (${data.failed} failed)` : ""));
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Send failed"); }
    setSending(false);
  };

  const sendTestToMe = async () => {
    if (!propertyId) return toast.error("Select a property first");
    setTestSending(true);
    try {
      const { data } = await api.post(
        `/admin/forum-digest/send-test?property_id=${propertyId}&variant=${chosenVariant}`);
      if (data.ok) {
        toast.success(`Test digest sent to ${data.sent_to} · via ${data.provider}`);
      } else {
        toast.error(`Test failed: ${data.error || "unknown"}`);
      }
    } catch (e) { toast.error(apiErrorMessage(e) || "Test send failed"); }
    setTestSending(false);
  };

  if (loading || !cfg) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 space-y-6" data-testid="forum-digest-page">
      <PageHeader
        eyebrow="Community"
        title="Forum Daily Digest"
        subtitle="Weekly email of top-liked forum threads and pinned announcements. Runs from your personal Resend account."
      />

      <Card className="p-6 border-white/10 bg-white/[0.02] space-y-5" data-testid="digest-config-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Digest schedule</div>
            <div className="text-xs text-zinc-500 mt-1">
              {cfg.enabled
                ? `Active — every ${DAY_NAMES[cfg.day_of_week] || "Monday"} at ${String(cfg.hour_local).padStart(2, "0")}:00 ${cfg.timezone || "UTC"}`
                : "Currently disabled — turn on the toggle to schedule automated sends"}
            </div>
          </div>
          <Switch checked={!!cfg.enabled}
                   onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                   data-testid="digest-enabled-toggle" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Day of week</Label>
            <Select value={String(cfg.day_of_week)}
                     onValueChange={(v) => setCfg({ ...cfg, day_of_week: parseInt(v) })}>
              <SelectTrigger data-testid="digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAY_NAMES.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hour (local, 0–23)</Label>
            <Input type="number" min="0" max="23"
                    value={cfg.hour_local ?? 8}
                    onChange={(e) => setCfg({ ...cfg, hour_local: e.target.value })}
                    data-testid="digest-hour-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1">
              <Globe className="h-3 w-3" /> Timezone
            </Label>
            <Select value={cfg.timezone || "UTC"}
                     onValueChange={(v) => setCfg({ ...cfg, timezone: v })}>
              <SelectTrigger data-testid="digest-tz-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">
                {tzOptions.map((tz) => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Featured threads</Label>
            <Input type="number" min="1" max="20"
                    value={cfg.top_n}
                    onChange={(e) => setCfg({ ...cfg, top_n: e.target.value })}
                    data-testid="digest-topn-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Minimum likes to include</Label>
            <Input type="number" min="0" max="100"
                    value={cfg.min_likes}
                    onChange={(e) => setCfg({ ...cfg, min_likes: e.target.value })}
                    data-testid="digest-minlikes-input" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="digest-save-btn">
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
          <Button variant="outline" onClick={doPreview} disabled={previewLoading}
                   className="gap-1.5 border-white/10" data-testid="digest-preview-btn">
            <Eye className="h-3.5 w-3.5" /> {previewLoading ? "Rendering…" : "Preview for current property"}
          </Button>
          <Button variant="outline" onClick={sendTestToMe} disabled={testSending}
                   className="gap-1.5 border-sky-500/30 text-sky-300 bg-sky-500/5 hover:bg-sky-500/10"
                   data-testid="digest-send-test-btn">
            <TestTube2 className="h-3.5 w-3.5" /> {testSending ? "Sending…" : "Send test to me"}
          </Button>
          <Button variant="outline" onClick={sendNow} disabled={sending}
                   className="gap-1.5 border-orange-500/30 text-orange-300 bg-orange-500/5 hover:bg-orange-500/10"
                   data-testid="digest-send-now-btn">
            <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send now (bypass schedule)"}
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-5 border-white/10 bg-white/[0.02] space-y-4" data-testid="digest-preview-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-medium">Preview · {preview.community}</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {preview.pinned_count} pinned · {preview.top_count} featured thread{preview.top_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex items-center gap-1 bg-white/[0.03] rounded-full p-1"
                 data-testid="variant-toggle">
              <button
                onClick={() => setChosenVariant(1)}
                className={`px-3 py-1 rounded-full text-xs transition ${
                  chosenVariant === 1
                    ? "bg-orange-500 text-white font-medium"
                    : "text-zinc-400 hover:text-white"}`}
                data-testid="variant-v1-btn"
              >V1 · warm</button>
              <button
                onClick={() => setChosenVariant(2)}
                className={`px-3 py-1 rounded-full text-xs transition ${
                  chosenVariant === 2
                    ? "bg-orange-500 text-white font-medium"
                    : "text-zinc-400 hover:text-white"}`}
                data-testid="variant-v2-btn"
              >V2 · curious</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[{ pv: preview, v: 1 }, { pv: previewV2, v: 2 }].map(({ pv, v }) => (
              <div
                key={v}
                className={`rounded-md border p-3 space-y-2 cursor-pointer transition ${
                  chosenVariant === v
                    ? "border-orange-500/60 bg-orange-500/10"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                onClick={() => setChosenVariant(v)}
                data-testid={`intro-card-v${v}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Variant {v} · {v === 1 ? "warm greeting" : "curious question"}
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px] hover:bg-white/10"
                    disabled={regenerating}
                    onClick={(e) => { e.stopPropagation(); regenerateIntro(v); }}
                    data-testid={`regenerate-v${v}-btn`}
                  >
                    {regenerating ? "…" : "Regenerate"}
                  </Button>
                </div>
                <div className="text-sm text-zinc-100 leading-relaxed"
                     data-testid={`intro-text-v${v}`}>
                  {pv?.ai_intro || <span className="text-zinc-500 italic">Loading…</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-md border border-white/10 overflow-hidden bg-white">
            <iframe title="Digest preview"
                     srcDoc={(chosenVariant === 1 ? preview : previewV2)?.html}
                     className="w-full h-[560px] bg-white"
                     data-testid="digest-preview-frame" />
          </div>
        </Card>
      )}

      <Card className="p-5 border-white/10 bg-white/[0.02]" data-testid="digest-runs-card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-3.5 w-3.5 text-zinc-500" />
          <div className="overline">Recent runs</div>
          <Badge variant="outline" className="ml-auto border-white/10 text-xs">{runs.length}</Badge>
        </div>
        {runs.length === 0 ? (
          <div className="text-xs text-zinc-500 py-6 text-center" data-testid="digest-runs-empty">
            <Rocket className="h-4 w-4 mx-auto mb-2 text-zinc-600" />
            No digest runs yet. Hit &quot;Send now&quot; to try one, or enable the schedule.
          </div>
        ) : (
          <div className="space-y-1.5">
            {runs.slice(0, 10).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-white/5"
                   data-testid={`digest-run-${i}`}>
                <span className="text-zinc-400 min-w-[10em]">{new Date(r.at).toLocaleString()}</span>
                <Badge variant="outline" className="text-[10px] border-white/10">
                  {r.actor}
                </Badge>
                <span className="text-emerald-300">✓ {r.sent}</span>
                {r.failed > 0 && <span className="text-rose-300">✗ {r.failed}</span>}
                <span className="text-zinc-500 ml-auto">{r.top_count} featured · {r.pinned_count} pinned</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <TestSendHistory propertyId={propertyId} />
    </div>
  );
}

// R138 — Digest sample-send history strip
function TestSendHistory({ propertyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/forum-digest/test-sends?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/ForumDigestPage.jsx: swallowed', e); }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5 border-white/10 bg-white/[0.02]" data-testid="test-sends-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="overline">Sample-send history</div>
        <Badge variant="outline" className="ml-auto border-white/10 text-xs">
          {items.length}
        </Badge>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center"
             data-testid="test-sends-empty">
          No test sends yet — hit &quot;Send test to me&quot; to start your A/B log.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {items.slice(0, 20).map((s, i) => (
            <div key={i}
                 className="flex items-center gap-2 text-xs py-1.5 border-b border-white/5"
                 data-testid={`test-send-${i}`}>
              <span className="text-zinc-400 min-w-[10em]">
                {new Date(s.at).toLocaleString()}
              </span>
              <Badge variant="outline"
                     className={`text-[10px] ${
                       s.variant === 2
                         ? "border-orange-500/30 text-orange-300"
                         : "border-sky-500/30 text-sky-300"}`}>
                V{s.variant} · {s.variant === 2 ? "curious" : "warm"}
              </Badge>
              <span className="text-zinc-500 truncate flex-1 italic">
                &ldquo;{(s.ai_intro || "").slice(0, 80)}
                {(s.ai_intro || "").length > 80 && "…"}&rdquo;
              </span>
              <span className={s.ok ? "text-emerald-300" : "text-rose-300"}>
                {s.ok ? "✓" : "✗"}
              </span>
              <span className="text-zinc-500 min-w-[5em] text-right">
                {s.admin_name}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
